import { useRef, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import {
  Button,
  Form,
  FormControl,
  Input,
  Label,
  Loader,
} from "@hydra/atom/components";
import { selectPermission } from "@/store/userSlice";
import { CustomActionDropdown } from "@/components/dynamic";
import { InfoTable } from "@/components/facility/maintenance";
import { getApprovalAssignmentColumns } from "@/components/facility/maintenance/infoTableData";

import {
  BoxedContent,
  Header,
  FormLeftHeader,
  Select,
} from "@/components/common";

import {
  createApprovalMap,
  updateApprovalMap,
  getApprovalMapById,
  deleteApprovalMap,
  getApprovalAssignments,
  createApprovalAssignment,
  updateApprovalAssignment,
  deleteApprovalAssignment,
} from "@/api/workflow/approvalMapApi";
import { getDynamicObjectRecords } from "@/api/dynamic/dynamicObjectNameApi";
import dynamicObjectMap from "@/utils/maps/dynamicObjectMap";
import showToast from "@/utils/toast/helpers";

const validationMessages = {
  required: {
    required: "This field is required",
  },
};

const prepareApprovalMapData = (state, id) => {
  const { approvalAssignment } = state;
  const firstApprover = approvalAssignment[0];
  const { employee } = firstApprover;

  const data = {
    approverMapName: employee.label,
  };

  if (id) {
    data.id = id;
  }

  return data;
};

const prepareApproverAssignmentData = (state, approverWorkflowMapId) => {
  const { approvalAssignment } = state;

  return approvalAssignment.map(({ employee, position }) => ({
    position: Number(position),
    employeeId: employee.value,
    isDelegated: false,
    approverWorkflowMapId,
  }));
};

const prepareApprovalMapState = (data) => data;

const prepareApproverAssignmentState = (data) => {
  const approvalAssignment = data
    .map(({ id, employee, position }) => ({
      id,
      position,
      employee: {
        label: employee.name,
        value: employee.id,
      },
    })).sort((a, b) => a.position - b.position);

  return {
    approvalAssignment,
  };
};

function ActionCell({ deleteMutation, id }) {
  const navigate = useNavigate();

  const actions = [
    {
      title: "View",
      onClick: () => {
        navigate(`/admin/approver-map/details/${id}`);
      },
      icon: "eye",
    },
    {
      title: "Delete",
      onClick: () => {
        deleteMutation.mutate(id);
      },
      icon: "trash-icon",
    },
  ];
  return (
    <div className="action-cell">
      <CustomActionDropdown
        actions={actions}
        trigger={(
          <div className="action-dropdown-trigger">
            <span className="text">Actions</span>
            <span className="material-icons-outlined">expand_more</span>
          </div>
        )}
        testId="Add-GL-Action-Button"
      />
    </div>
  );
}

const initialState = {
  number: "",
  approverMapName: "",
  approvalAssignment: [],
};

export default function ApprovalMapForm() {
  const [state, setState] = useState(initialState);
  const queryClient = useQueryClient();
  const deletePermission = useSelector(
    selectPermission({
      parent: "Model",
      scope: "ApprovalMap",
      action: "Delete",
    })
  );

  const approvalWorkflowSubmitButtonRef = useRef(null);
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();

  const { data, isInitialLoading } = useQuery(
    ["approval-map", id],
    () => getApprovalMapById(id),
    {
      enabled: isEditing,
    }
  );

  const {
    data: approverAssignmentData,
    isInitialLoading: isLoadingApproverAssignments,
  } = useQuery(
    ["approver-assignment", id],
    () => getApprovalAssignments({ approverWorkflowMapId: id }),
    {
      enabled: isEditing,
    }
  );

  useEffect(() => {
    if (isEditing && data) {
      const stateData = prepareApprovalMapState(data);
      setState((prevState) => ({
        ...prevState,
        ...stateData,
      }));
    }
  }, [data]);

  useEffect(() => {
    if (isEditing && approverAssignmentData) {
      const stateData = prepareApproverAssignmentState(
        approverAssignmentData?.data
      );
      setState((prevState) => ({
        ...prevState,
        ...stateData,
      }));
    }
  }, [approverAssignmentData]);

  const saveApproverAssignmentMutation = useMutation(createApprovalAssignment, {
    onError: () => {
      showToast("Could not create. Try again!", "error");
    },
  });

  const updateApproverAssignmentMutation = useMutation(
    ({ id: recordId, data: recordData }) =>
      updateApprovalAssignment(recordId, recordData),
    {
      onError: () => {
        showToast("Could not update. Try again!", "error");
      },
    }
  );

  const saveMutation = useMutation(createApprovalMap, {
    onError: (error) => {
      showToast(error.data, "error");
    },
    onSuccess: (response) => {
      const approvalMapId = response.data.id;

      const approvers = prepareApproverAssignmentData(state, approvalMapId);

      approvers.forEach((approver) => {
        saveApproverAssignmentMutation.mutate(approver);
      });

      showToast("Created successfully", "success");
      navigate(-1);
    },
  });

  const updateMutation = useMutation(
    ({ id: recordId, data: recordData }) =>
      updateApprovalMap(recordId, recordData),
    {
      onError: () => {
        showToast("Could not update. Try again!", "error");
      },
      onSuccess: () => {
        showToast("Updated successfully", "success");
        navigate(-1);
      },
    }
  );

  const deleteMutation = useMutation(deleteApprovalMap, {
    onError: () => {
      showToast("Could not delete. Try again!", "error");
    },
    onSuccess: () => {
      showToast("Deleted successfully", "success");
      queryClient.invalidateQueries({
        queryKey: ["approval-map"],
      });
    },
  });

  const deleteApproverAssignmentMutation = useMutation(
    deleteApprovalAssignment,
    {
      onError: () => {
        showToast("Could not delete. Try again!", "error");
      },
    }
  );

  const handleCancel = () => {
    setState({ ...initialState });
    navigate(-1);
  };

  const handleSave = () => {
    approvalWorkflowSubmitButtonRef.current.click();
  };

  const handleSubmit = async () => {
    const recordData = prepareApprovalMapData(state, id);

    if (!state.approvalAssignment.length) {
      showToast("Please add at least one approver!", "error");
      return;
    }

    if (isEditing) {
      updateMutation.mutate({
        id,
        data: recordData,
      });
      return;
    }

    saveMutation.mutate(recordData);
  };

  const onChange = (key, value) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleAddApproverAssigment = async () => {
    const { employee, approvalAssignment, position } = state;

    if (Number(position) === 0) {
      showToast("Position cannot be 0", "error");
      return;
    }

    if (Number(position) > approvalAssignment.length + 1) {
      showToast(
        `Position can only be added in the increment of 1. The next position is ${approvalAssignment.length + 1}.`,
        "error"
      );
      return;
    }

    const isEmployeeAlreadyAdded = approvalAssignment.find(
      ({ employee: approver }) => approver.value === employee.value
    );

    if (isEmployeeAlreadyAdded) {
      showToast("Employee already added", "error");
      return;
    }

    let approvalAssignmentId = position;
    let newApprovalAssignment = [];

    if (position <= approvalAssignment.length) {
      const updatableApprovalAssignments = approvalAssignment.filter(
        (a) => a.position >= position
      );

      if (isEditing) {
        try {
          await Promise.all(
            updatableApprovalAssignments.map((a) =>
              updateApproverAssignmentMutation.mutateAsync({
                id: a.id,
                data: {
                  employeeId: a.employee.value,
                  isDelegated: false,
                  approverWorkflowMapId: id,
                  position: Number(a.position) + 1,
                }
              })
            )
          );
        } catch (error) {
          showToast("Could not add. Please try again!");
        }
      }
    }

    if (isEditing) {
      const response = await saveApproverAssignmentMutation.mutateAsync({
        employeeId: employee.value,
        isDelegated: false,
        approverWorkflowMapId: id,
        position: Number(position),
      });

      approvalAssignmentId = response.data.id;
    }

    newApprovalAssignment = approvalAssignment.map((a) => {
      if (a.position >= position) {
        return {
          ...a,
          id: isEditing ? a.id : Number(a.position) + 1,
          position: Number(a.position) + 1,
        };
      }

      return a;
    });

    newApprovalAssignment.splice(position - 1, 0, {
      id: approvalAssignmentId,
      position: Number(position),
      employee,
    });

    onChange("approvalAssignment", newApprovalAssignment);
    onChange("employee", null);
    onChange("position", "");
  };

  const handleDeleteApprovalAssignment = async (approverAssignmentId) => {
    const { approvalAssignment } = state;

    const deletedApprover = approvalAssignment.find(
      (a) => a.id === approverAssignmentId
    );

    // If it is not last approver in the list
    if (deletedApprover.position !== approvalAssignment.length) {
      if (isEditing) {
        const clonedApprovalAssignment = [...approvalAssignment];

        const updatableApprovers = clonedApprovalAssignment.filter(
          (a) => a.position > deletedApprover.position
        );

        try {
          await Promise.all(
            updatableApprovers.map((a) =>
              updateApproverAssignmentMutation.mutateAsync({
                id: a.id,
                data: {
                  employeeId: a.employee.value,
                  isDelegated: false,
                  approverWorkflowMapId: id,
                  position: Number(a.position - 1),
                }
              })
            )
          );
        } catch (error) {
          showToast("Could not add. Please try again!");
        }
      }
    }

    if (isEditing) {
      await deleteApproverAssignmentMutation.mutateAsync(approverAssignmentId);
    }

    const updatedApprovalAssigment = [...approvalAssignment];

    const newApprovalAssignment = updatedApprovalAssigment.filter(
      ({ id: rowId }) => rowId !== approverAssignmentId
    ).map((a) => ({
      ...a,
      position: a.position > deletedApprover.position ? a.position - 1 : a.position
    }));

    onChange("approvalAssignment", newApprovalAssignment);
  };

  if (isEditing && isInitialLoading && isLoadingApproverAssignments) {
    return <Loader />;
  }

  return (
    <BoxedContent className="gl-account-form">
      <Header
        showBreadcrumb
        leftContent={<h1>{`${isEditing ? "Edit" : "Create"} Approval Map`}</h1>}
        rightContent={
          isEditing && deletePermission ? (
            <ActionCell deleteMutation={deleteMutation} id={id} />
          ) : null
        }
      />
      <Form
        onSubmit={handleSubmit}
        key="approval-map-form"
        className="dynamic-object-form"
        shouldScrollOnError
      >
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Name & Details"
              subtitle="Provide Name & Details of Approval Map"
              icon="scan-icon"
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <FormControl>
                  <Label htmlFor="number" label="Approver Map Number" />
                  <Input
                    name="number"
                    placeholder="Map-XXXX"
                    value={state.number}
                    testId="ApprovalMap-Number-Input"
                    disabled
                  />
                </FormControl>
              </div>
            </div>
          </div>

          <button
            type="submit"
            style={{ display: "none" }}
            ref={approvalWorkflowSubmitButtonRef}
          >
            Save
          </button>
        </div>
      </Form>
      <Form
        onSubmit={handleAddApproverAssigment}
        key="approval-assignment-form"
        className="dynamic-object-form"
        shouldScrollOnError
      >
        <div className="row">
          <div className="col-md-3">
            <FormLeftHeader
              title="Add approver"
              subtitle="Select next approver"
              icon="scan-icon"
            />
          </div>
          <div className="col-md-9">
            <div className="row">
              <div className="col-md-6">
                <FormControl>
                  <Label htmlFor="employee" label="Employee*" />
                  <Select
                    name="employee"
                    queryKey="employee"
                    placeholder="Select Employee"
                    value={state.employee}
                    onChange={(value) => onChange("employee", value)}
                    optionsLoader={() =>
                      getDynamicObjectRecords(
                        dynamicObjectMap.get("EmployeeObjectName")
                      )}
                    apiFilters={{
                      status: "Active",
                    }}
                    dataAccessKey="data"
                    optionLabelKey="name"
                    optionValueKey="id"
                    isClearable
                    rules="required"
                    validationMessages={validationMessages.required}
                  />
                </FormControl>
              </div>
              <div className="col-md-3">
                <FormControl>
                  <Label htmlFor="position" label="Position*" />
                  <Input
                    name="position"
                    queryKey="position"
                    placeholder="Add position"
                    value={state.position}
                    onChange={(value) => onChange("position", value)}
                    rules="required"
                    validationMessages={validationMessages.required}
                    testId="ApprovalMap-Position-Input"
                  />
                </FormControl>
              </div>
              <div className="col-md-3">
                <FormControl>
                  <Label label="Action" />
                  <Button small type="submit">
                    Add Approver
                  </Button>
                </FormControl>
              </div>

              {state.approvalAssignment.length ? (
                <div className="col-md-6">
                  <InfoTable
                    className="approval-assigment-table"
                    columns={getApprovalAssignmentColumns()}
                    data={state.approvalAssignment}
                    onDelete={handleDeleteApprovalAssignment}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </Form>
      <div className="col-md-12">
        <div className="hr" />
      </div>

      <div className="col">
        <div className="buttons-at-end">
          <Button
            bordered
            small
            onClick={handleCancel}
            testId="ApprovalMap-Cancel-Button"
          >
            Cancel
          </Button>
          <Button
            small
            className="save-btn"
            loading={saveMutation.isLoading || updateMutation.isLoading}
            onClick={handleSave}
            testId="ApprovalMap-Submit-Button"
          >
            Save
          </Button>
        </div>
      </div>
    </BoxedContent>
  );
}
